import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Send } from 'lucide-react';
import { api } from '../../api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { ErrorNote, StatusDot, Transcript } from '../../components/kit';
import { Attachments, type Attached } from '../../components/attachments';

/** A worker is late when it has missed several heartbeats, not one. */
export const LATE_AFTER_MS = 3 * 60_000;

export const SELECT =
  'h-7 rounded-[5px] border border-rule bg-field px-2 text-[12.5px] text-ink outline-none transition-[border-color,box-shadow] hover:border-rule-strong focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/25';

export function Count({ value, live = false }: { value?: number; live?: boolean }) {
  if (value === undefined) return null;
  return (
    <span
      className={`inline-flex min-w-[1.25rem] items-center justify-center gap-1 rounded-[4px] px-1 py-px text-[11px] tabular-nums ${
        live ? 'bg-run-soft text-run' : 'bg-raised text-ink-3'
      }`}
    >
      {live && <StatusDot state="running" />}
      {value}
    </span>
  );
}

/**
 * The state of the installation in one line. Metered spend and subscription
 * equivalence are never added together — they are not the same quantity.
 */

export function RunTranscript({ id, live = false }: { id: string; live?: boolean }) {
  const query = useQuery({
    queryKey: ['transcript', id],
    queryFn: () =>
      api.get<{ text: string; running: boolean; ok: boolean | null; reason: string | null }>(
        `/api/runs/${id}/transcript`,
      ),
    refetchInterval: live ? 3000 : false,
  });
  if (query.isPending) return <Skeleton className="mt-2 h-24 w-full" />;

  const run = query.data;
  const text = run?.text ?? '';
  if (text === '' && (live || run?.running)) {
    // The worker publishes every few seconds; before the first turn there is
    // genuinely nothing, and the agent may still be cloning or installing.
    return (
      <p className="mt-2 rounded-[5px] border border-rule bg-raised px-3 py-2 text-[12px] text-ink-2">
        Waiting for the agent's first output — a fresh worktree clones the repository and runs the setup
        command before the model starts.
      </p>
    );
  }
  if (text === '' && run?.ok === false && run.reason) {
    // A run can fail before the agent writes a line — no handler for the status,
    // a repository it cannot clone. The transcript is where somebody goes looking
    // for the failure, so the failure has to be there.
    return (
      <p
        role="alert"
        className="mt-2 rounded-[5px] border border-bad/30 bg-bad-soft px-3 py-2 text-[12px] text-bad"
      >
        This run wrote nothing. It failed: {run.reason}
      </p>
    );
  }
  return <Transcript text={text} />;
}

export function Feedback({
  taskId,
  tenantId,
  onSent,
}: {
  taskId: string;
  tenantId?: string;
  onSent: () => void;
}) {
  const [text, setText] = useState('');
  const [images, setImages] = useState<Attached[]>([]);
  const send = useMutation({
    mutationFn: () =>
      api.post(`/api/tasks/${taskId}/feedback`, { text, attachmentIds: images.map((image) => image.id) }),
    onSuccess: onSent,
  });
  return (
    <div className="flex flex-col gap-2 rounded-[5px] border border-rule bg-raised px-3 py-2.5">
      <p className="label-caps">Send back to the agent — it continues on the same branch</p>
      <Textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="What should it fix or do differently?"
        className="bg-panel"
      />
      {/* "Here is what it looks like now" is the commonest thing to send back. */}
      <Attachments tenantId={tenantId} value={images} onChange={setImages} />
      <div>
        <Button size="sm" disabled={!text.trim() || send.isPending} onClick={() => send.mutate()}>
          <Send className="size-3.5" aria-hidden /> Send back
        </Button>
      </div>
      <ErrorNote error={send.error} />
    </div>
  );
}

/**
 * What a task carries, shown where its description is read. Fetched only when
 * somebody opens the detail — a queue of thumbnails would pull every image out
 * of Postgres to render a list of titles.
 */
export function TaskAttachments({ taskId }: { taskId: string }) {
  const query = useQuery({
    queryKey: ['attachments', taskId],
    queryFn: () => api.get<{ id: string; name: string }[]>(`/api/tasks/${taskId}/attachments`),
  });
  const images = query.data ?? [];
  if (images.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {images.map((image) => (
        <a key={image.id} href={`/api/attachments/${image.id}`} target="_blank" rel="noreferrer">
          <img
            src={`/api/attachments/${image.id}`}
            alt={image.name}
            title={image.name}
            className="size-20 rounded-[5px] border border-rule object-cover transition-[border-color] hover:border-rule-strong"
          />
        </a>
      ))}
    </div>
  );
}
